import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldCheckbox, NamedLink, ForgetPasswordModal, InlineTextButton, IconClose } from '../../../components';

import css from './LoginForm.module.css';

const LoginFormComponent = props => {
  const {
    onManageDisableScrolling,
    ...otherProps
  } = props;

  // Load saved email from localStorage on mount
  const [savedEmail] = React.useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem('rememberedEmail') || '';
    }
    return '';
  });

  const emailInputRef = useRef(null);

  // State for forget password modal
  const [isForgetPasswordModalOpen, setIsForgetPasswordModalOpen] = useState(false);

  return (
    <FinalForm
      {...otherProps}
      onSubmit={(values) => {
        // Handle form submission with "Remember Me" functionality
        // Handle "Remember me" functionality - save or remove email
        // FieldCheckbox returns an array when checked: ['rememberMe']
        const isRememberMeChecked = Array.isArray(values.rememberMe) && values.rememberMe.length > 0;
        if (typeof window !== 'undefined' && window.localStorage) {
          if (isRememberMeChecked) {
            localStorage.setItem('rememberedEmail', values.email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }
        }

        // Call the original handleSubmit
        return props.onSubmit(values);
      }}
      keepDirtyOnReinitialize
      initialValues={{
        email: savedEmail,
        rememberMe: savedEmail ? ['rememberMe'] : [],
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          showAlmostThereMessage,
          values,
          form,
        } = fieldRenderProps;

        // email
        const emailLabel = intl.formatMessage({
          id: 'LoginForm.emailLabel',
        });
        const emailPlaceholder = intl.formatMessage({
          id: 'LoginForm.emailPlaceholder',
        });
        const emailRequiredMessage = intl.formatMessage({
          id: 'LoginForm.emailRequired',
        });
        const emailRequired = validators.required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({
          id: 'LoginForm.emailInvalid',
        });
        const emailValid = validators.emailFormatValid(emailInvalidMessage);

        // password
        const passwordLabel = intl.formatMessage({
          id: 'LoginForm.passwordLabel',
        });
        const passwordPlaceholder = intl.formatMessage({
          id: 'LoginForm.passwordPlaceholder',
        });
        const passwordRequiredMessage = intl.formatMessage({
          id: 'LoginForm.passwordRequired',
        });
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);



        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        const clearEmailButton = values.email ? (
          <button
            type="button"
            className={css.clearEmailButton}
            aria-label={intl.formatMessage({ id: 'LoginForm.clearEmail' })}
            onMouseDown={e => {
              e.preventDefault();
              form.change('email', '');
              if (emailInputRef.current) {
                emailInputRef.current.focus();
              }
            }}
          >
            <IconClose />
          </button>
        ) : null;

        const passwordRecoveryLink = (
          <InlineTextButton
            onClick={() => setIsForgetPasswordModalOpen(true)}
            className={css.recoveryLink}
          >
            <FormattedMessage id="LoginForm.forgotPassword" />
          </InlineTextButton>
        );

        // const almostThere = showAlmostThereMessage ? (
        //   <>
        //     <p className={css.heading}>
        //       <FormattedMessage id="LoginForm.almostThereHeading" defaultMessage="Almost There!" />
        //     </p>
        //     <p className={css.subheading}>
        //       <FormattedMessage id="LoginForm.almostThereSubheading" defaultMessage="Create an account or log in, and we will take you back to the page you came from." />
        //     </p>
        //   </>
        // ) : null;

        return (
          <>
            <Form keepDirtyOnReinitialize className={classes} onSubmit={handleSubmit}>
              <div>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={emailLabel}
                  placeholder={emailPlaceholder}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  iconLeft={<i className='ri-mail-line'></i>}
                  iconRight={clearEmailButton}
                  inputRef={emailInputRef}
                />
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="current-password"
                  label={passwordLabel}
                  placeholder={passwordPlaceholder}
                  validate={passwordRequired}
                  iconLeft={<i className='ri-lock-2-line'></i>}
                />
              </div>

              <div className={css.loginOptions}>
                <div className={css.rememberMeWrapper}>
                  <FieldCheckbox
                    id={formId ? `${formId}.rememberMe` : 'rememberMe'}
                    name="rememberMe"
                    label={intl.formatMessage({
                      id: 'LoginForm.rememberMe',
                    })}
                    value="rememberMe"
                    useSuccessColor
                  />
                </div>
                <InlineTextButton
                  onClick={() => setIsForgetPasswordModalOpen(true)}
                  className={css.forgotPasswordLink}
                  type="button"
                >
                  <FormattedMessage id="LoginForm.forgotPassword" />
                </InlineTextButton>
              </div>

              <div className={css.bottomWrapper}>
                <PrimaryButton className={css.loginBtn} type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                  <FormattedMessage id="LoginForm.logIn" />
                </PrimaryButton>
              </div>
            </Form>

            <ForgetPasswordModal
              isOpen={isForgetPasswordModalOpen}
              onClose={() => setIsForgetPasswordModalOpen(false)}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </>
        );
      }}
    />
  );
};

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  onManageDisableScrolling: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
